import React, { useState, useEffect } from "react";
import Header from "../Header";
import Footer from "../Footer";
import API_BASE_URL from "../../Config";
import { useParams } from "react-router-dom";
import Select from "react-select";

/* ────────── SVG Icon System ────────── */
const Icon = ({ d, size = 18, stroke = 1.8 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={stroke}
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ display: "block", flexShrink: 0 }}
  >
    {d}
  </svg>
);

const ClockIcon = (p) => (
  <Icon {...p} d={<><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></>} />
);
const CalendarIcon = (p) => (
  <Icon {...p} d={<><rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></>} />
);
const PlusIcon = (p) => (
  <Icon {...p} d={<><path d="M12 5v14M5 12h14" /></>} />
);
const TrashIcon = (p) => (
  <Icon {...p} d={<><path d="M3 6h18" /><path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /><path d="M19 6 18 20a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6M14 11v6" /></>} />
);
const SaveIcon = (p) => (
  <Icon {...p} d={<><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" /><polyline points="17 21 17 13 7 13 7 21" /><polyline points="7 3 7 8 15 8" /></>} />
);
const UserIcon = (p) => (
  <Icon {...p} d={<><circle cx="12" cy="8" r="4" /><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" /></>} />
);
const CheckIcon = (p) => (
  <Icon {...p} d={<><path d="M20 6 9 17l-5-5" /></>} />
);
const CheckCircleIcon = (p) => (
  <Icon {...p} d={<><circle cx="12" cy="12" r="10" /><path d="m9 12 2 2 4-4" /></>} />
);
const XCircleIcon = (p) => (
  <Icon {...p} d={<><circle cx="12" cy="12" r="10" /><path d="m15 9-6 6M9 9l6 6" /></>} />
);
const CloseIcon = (p) => (
  <Icon {...p} d={<><path d="M18 6 6 18M6 6l12 12" /></>} />
);

/* ────────── Toast ────────── */
const SuccessToast = ({ message, onClose }) => (
  <div className="av-toast av-toast--success" role="alert">
    <span className="av-toast__icon"><CheckCircleIcon size={20} /></span>
    <span className="av-toast__msg">{message}</span>
    <button className="av-toast__close" onClick={onClose} type="button">
      <CloseIcon size={15} />
    </button>
  </div>
);
const ErrorToast = ({ message, onClose }) => (
  <div className="av-toast av-toast--error" role="alert">
    <span className="av-toast__icon"><XCircleIcon size={20} /></span>
    <span className="av-toast__msg">{message}</span>
    <button className="av-toast__close" onClick={onClose} type="button">
      <CloseIcon size={15} />
    </button>
  </div>
);

/* ────────── Section Card ────────── */
const SectionCard = ({ title, icon, children }) => (
  <div className="av-section-card">
    <div className="av-section-header">
      <div className="av-section-icon">{icon}</div>
      <span className="av-section-title">{title}</span>
    </div>
    <div className="av-section-body">{children}</div>
  </div>
);

/* ────────── react-select styles ────────── */
const selectStyles = {
  control: (base, state) => ({
    ...base,
    borderRadius: "10px",
    border: `1px solid ${state.isFocused ? "#10b981" : "#e3f0e9"}`,
    boxShadow: state.isFocused ? "0 0 0 4px rgba(16,185,129,.12)" : "none",
    background: "#f9fcfb",
    minHeight: "46px",
    fontSize: "14px",
    cursor: "pointer",
    "&:hover": { borderColor: "#10b981" },
    transition: "border-color .15s, box-shadow .15s",
  }),
  menu: (base) => ({
    ...base,
    borderRadius: "12px",
    border: "1px solid #e3f0e9",
    boxShadow: "0 12px 32px rgba(16,30,24,.12)",
    overflow: "hidden",
    zIndex: 9999,
  }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  option: (base, state) => ({
    ...base,
    background: state.isSelected ? "#10b981" : state.isFocused ? "#ecfdf5" : "#fff",
    color: state.isSelected ? "#fff" : "#0f1f1a",
    fontSize: "14px",
    padding: "10px 14px",
    cursor: "pointer",
  }),
  placeholder: (base) => ({ ...base, color: "#9aa8a2", fontSize: "14px" }),
  singleValue: (base) => ({ ...base, color: "#0f1f1a" }),
};

/* ────────── Day colour map ────────── */
const DAY_COLORS = {
  Monday:    { bg: "#ede9fe", color: "#6d28d9", dot: "#8b5cf6" },
  Tuesday:   { bg: "#fef3c7", color: "#92400e", dot: "#f59e0b" },
  Wednesday: { bg: "#dbeafe", color: "#1e40af", dot: "#3b82f6" },
  Thursday:  { bg: "#d1fae5", color: "#065f46", dot: "#10b981" },
  Friday:    { bg: "#fce7f3", color: "#9d174d", dot: "#ec4899" },
  Saturday:  { bg: "#ffedd5", color: "#c2410c", dot: "#f97316" },
  Sunday:    { bg: "#fee2e2", color: "#991b1b", dot: "#ef4444" },
};

const getDayStyle = (day) =>
  DAY_COLORS[day] || { bg: "#f1f5f9", color: "#475569", dot: "#94a3b8" };

/* ────────── Format time for display ────────── */
const formatTime = (t) => {
  if (!t) return "";
  const [h, m] = t.split(":").map(Number);
  const ampm = h >= 12 ? "PM" : "AM";
  const hour = h % 12 || 12;
  return `${hour}:${String(m).padStart(2, "0")} ${ampm}`;
};

/* ────────── Main Component ────────── */
const DoctorAvailabilityAdd = () => {
  const token = localStorage.getItem("authToken");
  const { id } = useParams();

  const [doctors, setDoctors] = useState([]);
  const [doctor, setDoctor] = useState(null);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState({ show: false, type: "", message: "" });

  const [availability, setAvailability] = useState([
    { day: "", from: "", to: "" },
  ]);

  const flashToast = (message, type = "success") => {
    setToast({ show: true, type, message });
    setTimeout(() => setToast({ show: false, type: "", message: "" }), 4500);
  };

  useEffect(() => {
    fetchDoctors();
    if (id) fetchAvailability();
  }, []);

  /* ── Fetch ALL doctors (not just the default paginated page) ──
     api/doctors is paginated on the Doctor List page (page/per_page params).
     Without explicit params it was falling back to a default page size,
     so doctors beyond the first page (e.g. clicking "Availability" from
     page 2 of the Doctor List) were missing from this dropdown's option
     list, causing setDoctor(...) to resolve to undefined and the whole
     form to look stale/empty. Requesting a very large per_page guarantees
     every doctor is present so the id-based lookup below always finds
     a match. ── */
  const fetchDoctors = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}api/doctors?page=1&per_page=99999`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.status) {
        const doctorOptions = data.data.map((d) => ({ value: d.id, label: d.name }));
        setDoctors(doctorOptions);
        if (id) {
          const selectedDoctor = doctorOptions.find((doc) => doc.value.toString() === id);
          setDoctor(selectedDoctor);
        }
      }
    } catch {}
  };

  const fetchAvailability = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}api/doctor-availabilityfetch/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.status && data.data.length > 0) {
        setAvailability(data.data.map((d) => ({ day: d.day, from: d.from_time, to: d.to_time })));
      }
    } catch {}
  };

  const handleAvailabilityChange = (index, field, value) => {
    const updated = [...availability];
    updated[index][field] = value;
    setAvailability(updated);
  };

  const addSlot = () => {
    setAvailability([...availability, { day: "", from: "", to: "" }]);
  };

  const removeSlot = (index) => {
    setAvailability(availability.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = { doctor_id: doctor?.value, availability };
      const res = await fetch(`${API_BASE_URL}api/doctor-availability`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (data.status) {
        flashToast("Availability saved successfully!", "success");
      } else {
        flashToast("Failed to save availability.", "error");
      }
    } catch {
      flashToast("Something went wrong. Please try again.", "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <style>{`
        :root {
          --bc-bg:           #f3faf6;
          --bc-card:         #ffffff;
          --bc-border:       #e3f0e9;
          --bc-text:         #0f1f1a;
          --bc-muted:        #6b7a74;
          --bc-primary:      #10b981;
          --bc-primary-d:    #059669;
          --bc-primary-grad: linear-gradient(135deg, #14b8a6 0%, #10b981 100%);
          --bc-soft:         #ecfdf5;
          --bc-del:          #fee4e2;
          --bc-del-fg:       #e0413c;
          --bc-shadow:       0 1px 2px rgba(16,30,24,.04), 0 4px 14px rgba(16,30,24,.04);
        }

        .av-shell { background: var(--bc-bg); min-height: 100%; padding: 24px; }

        /* ── Page Header ── */
        .av-head {
          display: flex; align-items: flex-start;
          justify-content: space-between; gap: 16px;
          flex-wrap: wrap; margin-bottom: 24px;
        }
        .av-head-left { display: flex; align-items: center; gap: 14px; }
        .av-head-icon {
          width: 48px; height: 48px;
          background: var(--gradient-brand); border-radius: 14px;
          display: flex; align-items: center; justify-content: center;
          box-shadow: 0 6px 18px rgba(16,185,129,.28); flex-shrink: 0;
        }
        .av-head-icon svg { fill: none !important; color: #fff; }
        .av-head h2 {
          font-size: clamp(20px, 2.2vw, 28px); font-weight: 800;
          color: var(--bc-text); margin: 0 0 3px; letter-spacing: -0.01em;
        }
        .av-head p { color: var(--bc-muted); font-size: 14px; margin: 0; }

        /* ── Toast ── */
        .av-toast {
          display: flex; align-items: center; gap: 12px;
          padding: 14px 18px; border-radius: 14px;
          margin: 0 0 20px; font-size: 14px; font-weight: 500;
          animation: avDropIn .3s cubic-bezier(.34,1.56,.64,1);
          box-shadow: 0 4px 20px rgba(0,0,0,.09);
          position: sticky; top: 12px; z-index: 100;
        }
        .av-toast--success { background: #ecfdf5; color: #065f46; border: 1px solid #b7e8d2; }
        .av-toast--error   { background: #fff1f0; color: #991b1b; border: 1px solid #fbd7d3; }
        .av-toast__icon svg { fill: none !important; }
        .av-toast__msg { flex: 1; }
        .av-toast__close {
          display: inline-flex; align-items: center; justify-content: center;
          background: transparent; border: none; cursor: pointer;
          color: inherit; opacity: .65; padding: 4px; border-radius: 6px;
          transition: opacity .15s;
        }
        .av-toast__close:hover { opacity: 1; }
        .av-toast__close svg { fill: none !important; }

        /* ── Section Card ── */
        .av-section-card {
          background: var(--bc-card); border: 1px solid var(--color-border);
          border-radius: 18px; box-shadow: var(--bc-shadow);
          overflow: visible; margin-bottom: 20px;
        }
        .av-section-header {
          display: flex; align-items: center; gap: 12px;
          padding: 18px 22px; border-bottom: 1px solid #f0f6f3;
          background: linear-gradient(135deg, #ecfdf5 0%, #f0fbf6 100%);
          border-radius: 18px 18px 0 0;
        }
        .av-section-icon {
          width: 36px; height: 36px;
          background: var(--gradient-brand); border-radius: 9px;
          display: flex; align-items: center; justify-content: center;
          box-shadow: 0 4px 10px rgba(16,185,129,.25); flex-shrink: 0;
        }
        .av-section-icon svg { fill: none !important; color: #fff; }
        .av-section-title { font-size: 15px; font-weight: 700; color: var(--bc-text); }
        .av-section-body { padding: 22px; }

        /* ── Doctor Select grid ── */
        .av-doctor-grid { max-width: 480px; }
        .av-label { font-size: 13px; font-weight: 600; color: #3d4a45; margin-bottom: 7px; display: block; }

        /* ── Slot cards ── */
        .av-slots-list { display: flex; flex-direction: column; gap: 16px; }

        .av-slot-card {
          border: 1px solid var(--color-border); border-radius: 14px;
          overflow: visible; background: #fff;
          box-shadow: 0 1px 4px rgba(16,30,24,.04);
          transition: box-shadow .2s ease;
        }
        .av-slot-card:hover { box-shadow: 0 4px 14px rgba(16,30,24,.08); }

        .av-slot-top {
          display: flex; align-items: center; justify-content: space-between;
          padding: 12px 18px; border-bottom: 1px solid #f0f6f3;
          background: #fafefb; border-radius: 14px 14px 0 0;
        }
        .av-slot-top-left { display: flex; align-items: center; gap: 10px; }

        .av-slot-num {
          font-size: 11px; font-weight: 700; letter-spacing: .06em;
          padding: 4px 10px; border-radius: 999px;
          background: var(--bc-soft); color: var(--bc-primary-d);
          border: 1px solid #b7e8d2; text-transform: uppercase;
        }
        .av-day-badge {
          display: inline-flex; align-items: center; gap: 6px;
          padding: 4px 12px; border-radius: 999px;
          font-size: 12px; font-weight: 700; letter-spacing: .04em;
          text-transform: uppercase; transition: all .2s;
        }
        .av-day-dot {
          width: 7px; height: 7px; border-radius: 50%; flex-shrink: 0;
        }

        .av-slot-body { padding: 18px; }

        .av-slot-fields {
          display: grid;
          grid-template-columns: 1fr 1fr 1fr auto;
          gap: 14px;
          align-items: end;
        }

        .av-field { display: flex; flex-direction: column; gap: 6px; }
        .av-field-label {
          font-size: 11.5px; font-weight: 700; letter-spacing: .07em;
          text-transform: uppercase; color: var(--bc-muted);
          display: flex; align-items: center; gap: 6px;
        }
        .av-field-label svg { fill: none !important; color: var(--bc-primary-grad); }

        .av-select-native {
          width: 100%; box-sizing: border-box;
          padding: 11px 36px 11px 14px; border-radius: 10px;
          border: 1px solid var(--color-border); background: #f9fcfb;
          font-size: 14px; color: var(--bc-text); outline: none;
          appearance: none;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%239aa8a2' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E");
          background-repeat: no-repeat; background-position: right 10px center;
          cursor: pointer; font-family: inherit;
          transition: border-color .15s, box-shadow .15s;
        }
        .av-select-native:focus {
          border-color: var(--bc-primary-grad); background-color: #fff;
          box-shadow: 0 0 0 4px rgba(16,185,129,.12);
        }

        .av-time-input {
          width: 100%; box-sizing: border-box;
          padding: 11px 14px; border-radius: 10px;
          border: 1px solid var(--color-border); background: #f9fcfb;
          font-size: 14px; color: var(--bc-text); outline: none;
          font-family: inherit; cursor: pointer;
          transition: border-color .15s, box-shadow .15s;
        }
        .av-time-input:focus {
          border-color: var(--bc-primary-grad); background: #fff;
          box-shadow: 0 0 0 4px rgba(16,185,129,.12);
        }

        /* ── Delete button ── */
        .av-del-btn {
          display: inline-flex; align-items: center; justify-content: center;
          gap: 6px; padding: 11px 16px; border-radius: 10px;
          background: var(--bc-del); color: var(--bc-del-fg);
          border: 1px solid #fbcfcd; font-size: 13px; font-weight: 600;
          cursor: pointer; white-space: nowrap;
          transition: filter .15s, transform .15s;
        }
        .av-del-btn svg { fill: none !important; }
        .av-del-btn:hover { filter: brightness(.93); transform: translateY(-1px); }

        /* ── Preview strip ── */
        .av-slot-preview {
          margin: 0 18px 16px;
          padding: 10px 16px;
          border-radius: 10px;
          border: 1.5px dashed #b7e8d2;
          background: #f6fcf9;
          font-size: 13px; color: var(--bc-muted);
          display: flex; align-items: center; gap: 8px;
        }
        .av-slot-preview svg { fill: none !important; color: var(--bc-primary-grad); flex-shrink: 0; }
        .av-slot-preview span { color: var(--bc-text); }
        .av-slot-preview .av-preview-day  { font-weight: 700; }
        .av-slot-preview .av-preview-time { color: var(--bc-primary-d); font-weight: 600; }

        /* ── Add slot button ── */
        .av-add-slot-btn {
          width: 100%;
          display: flex; align-items: center; justify-content: center; gap: 10px;
          padding: 16px;
          border: 2px dashed var(--color-border); border-radius: 14px;
          background: transparent; color: var(--bc-muted);
          font-size: 14px; font-weight: 600; cursor: pointer;
          transition: all .2s ease;
        }
        .av-add-slot-btn svg { fill: none !important; }
        .av-add-slot-btn:hover {
          border-color: var(--bc-primary-grad); color: var(--bc-primary-grad);
          background: var(--bc-soft);
        }

        /* ── Submit bar ── */
        .av-submit-bar {
          display: flex; align-items: center; justify-content: flex-end;
          gap: 12px; padding: 20px 0 4px;
        }
        .av-btn-submit {
          display: inline-flex; align-items: center; gap: 8px;
          padding: 13px 28px; border-radius: 12px;
          background: var(--gradient-brand); color: #fff;
          border: none; font-size: 15px; font-weight: 700; cursor: pointer;
          box-shadow: 0 6px 18px rgba(16,185,129,.25);
          transition: filter .15s, box-shadow .15s, transform .15s;
        }
        .av-btn-submit svg { fill: none !important; }
        .av-btn-submit:hover { filter: brightness(1.05); box-shadow: 0 10px 24px rgba(16,185,129,.3); transform: translateY(-1px); }
        .av-btn-submit:active { transform: translateY(0); }
        .av-btn-submit:disabled { opacity: .6; cursor: not-allowed; transform: none; }

        /* ── Loading overlay ── */
        .av-overlay {
          position: fixed; inset: 0; z-index: 1080;
          background: rgba(255,255,255,.55); backdrop-filter: blur(2px);
          display: grid; place-items: center;
        }
        .av-spinner {
          width: 42px; height: 42px; border-radius: 50%;
          border: 3px solid #d6efe2; border-top-color: var(--bc-primary-grad);
          animation: avSpin .8s linear infinite;
        }

        /* ── Animations ── */
        @keyframes avDropIn { from { opacity: 0; transform: translateY(-6px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes avSpin { to { transform: rotate(360deg); } }
        @keyframes avSlideIn { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); } }
        .av-slot-card { animation: avSlideIn .22s ease; }

        /* ── Responsive ── */
        @media (max-width: 900px) {
          .av-slot-fields { grid-template-columns: 1fr 1fr; }
          .av-del-btn { grid-column: 1 / -1; justify-content: center; }
        }
        @media (max-width: 600px) {
          .av-shell { padding: 14px; }
          .av-slot-fields { grid-template-columns: 1fr; }
          .av-del-btn { grid-column: 1; }
          .av-submit-bar { flex-direction: column-reverse; }
          .av-btn-submit { width: 100%; justify-content: center; }
        }

        .av-shell svg { fill: none; }
      `}</style>

      <div id="layout-wrapper">
        <Header />

        {loading && (
          <div className="av-overlay">
            <div className="av-spinner" />
          </div>
        )}

        <div className="main-content">
          <div className="page-content">
            <div className="av-shell">

              {/* ── Page Header ── */}
              <div className="av-head">
                <div className="av-head-left">
                  <div className="av-head-icon">
                    <ClockIcon size={22} />
                  </div>
                  <div>
                    <h2>Doctor Availability</h2>
                    <p>Configure weekly availability slots for the doctor.</p>
                  </div>
                </div>
              </div>

              {/* ── Toast ── */}
              {toast.show && toast.type === "success" && (
                <SuccessToast message={toast.message} onClose={() => setToast({ show: false, type: "", message: "" })} />
              )}
              {toast.show && toast.type === "error" && (
                <ErrorToast message={toast.message} onClose={() => setToast({ show: false, type: "", message: "" })} />
              )}

              <form onSubmit={handleSubmit}>

                {/* ── Doctor Selector ── */}
                <SectionCard title="Select Doctor" icon={<UserIcon size={17} />}>
                  <div className="av-doctor-grid">
                    <label className="av-label">Doctor</label>
                    <Select
                      options={doctors}
                      value={doctor}
                      onChange={(selected) => setDoctor(selected)}
                      styles={selectStyles}
                      menuPortalTarget={document.body}
                      placeholder="Search and select a doctor…"
                      isClearable
                    />
                  </div>
                </SectionCard>

                {/* ── Availability Slots ── */}
                <SectionCard title="Availability Slots" icon={<CalendarIcon size={17} />}>

                  <div className="av-slots-list">
                    {availability.map((slot, index) => {
                      const dayStyle = getDayStyle(slot.day);
                      const hasPreview = slot.day && slot.from && slot.to;

                      return (
                        <div className="av-slot-card" key={index}>

                          {/* Slot header */}
                          <div className="av-slot-top">
                            <div className="av-slot-top-left">
                              <span className="av-slot-num">Slot #{index + 1}</span>
                              {slot.day && (
                                <span
                                  className="av-day-badge"
                                  style={{ background: dayStyle.bg, color: dayStyle.color }}
                                >
                                  <span className="av-day-dot" style={{ background: dayStyle.dot }} />
                                  {slot.day.toUpperCase()}
                                </span>
                              )}
                            </div>
                            {availability.length > 1 && (
                              <button
                                type="button"
                                className="av-del-btn"
                                onClick={() => removeSlot(index)}
                                title="Remove slot"
                              >
                                <TrashIcon size={14} />
                                Delete
                              </button>
                            )}
                          </div>

                          {/* Slot fields */}
                          <div className="av-slot-body">
                            <div className="av-slot-fields">

                              <div className="av-field">
                                <label className="av-field-label">
                                  <CalendarIcon size={13} /> Day
                                </label>
                                <select
                                  className="av-select-native"
                                  value={slot.day}
                                  onChange={(e) => handleAvailabilityChange(index, "day", e.target.value)}
                                >
                                  <option value="">Select Day</option>
                                  <option>Monday</option>
                                  <option>Tuesday</option>
                                  <option>Wednesday</option>
                                  <option>Thursday</option>
                                  <option>Friday</option>
                                  <option>Saturday</option>
                                  <option>Sunday</option>
                                </select>
                              </div>

                              <div className="av-field">
                                <label className="av-field-label">
                                  <ClockIcon size={13} /> From Time
                                </label>
                                <input
                                  type="time"
                                  className="av-time-input"
                                  value={slot.from}
                                  onChange={(e) => handleAvailabilityChange(index, "from", e.target.value)}
                                />
                              </div>

                              <div className="av-field">
                                <label className="av-field-label">
                                  <ClockIcon size={13} /> To Time
                                </label>
                                <input
                                  type="time"
                                  className="av-time-input"
                                  value={slot.to}
                                  onChange={(e) => handleAvailabilityChange(index, "to", e.target.value)}
                                />
                              </div>

                              {/* Delete on wider screens (inside grid) */}
                              <div className="av-field" style={{ justifyContent: "flex-end" }}>
                                <label className="av-field-label" style={{ opacity: 0, userSelect: "none" }}>‌</label>
                                {availability.length > 1 ? (
                                  <button
                                    type="button"
                                    className="av-del-btn"
                                    onClick={() => removeSlot(index)}
                                    style={{ display: "none" }}
                                  />
                                ) : <div style={{ height: "46px" }} />}
                              </div>

                            </div>
                          </div>

                          {/* Preview strip */}
                          {hasPreview && (
                            <div className="av-slot-preview">
                              <CheckIcon size={15} />
                              Available on&nbsp;
                              <span className="av-preview-day" style={{ color: dayStyle.color }}>
                                {slot.day}
                              </span>
                              &nbsp;from&nbsp;
                              <span className="av-preview-time">{formatTime(slot.from)}</span>
                              &nbsp;to&nbsp;
                              <span className="av-preview-time">{formatTime(slot.to)}</span>
                            </div>
                          )}

                        </div>
                      );
                    })}

                    {/* Add slot */}
                    <button type="button" className="av-add-slot-btn" onClick={addSlot}>
                      <PlusIcon size={18} />
                      Add New Slot
                    </button>
                  </div>

                </SectionCard>

                {/* ── Submit bar ── */}
                <div className="av-submit-bar">
                  <button type="submit" className="av-btn-submit" disabled={loading}>
                    <SaveIcon size={17} />
                    {loading ? "Saving…" : "Save Availability"}
                  </button>
                </div>

              </form>

            </div>
          </div>
          <Footer />
        </div>
      </div>
    </>
  );
};

export default DoctorAvailabilityAdd;